import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, DrawingManagerF, MarkerF, CircleF, PolygonF, RectangleF, PolylineF, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api';
import { Trash2, Save, CheckCircle, XCircle, PlusCircle, RefreshCw } from 'lucide-react';
import { listRoute } from '../Route/_requests';
import { createDeliveryLocation } from './_requests';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader, 
  Button, 
  Input,
  InputGroup
} from 'reactstrap';
import './GeofenceMap.css';
import { useProfile } from "../../Components/Hooks/UserHooks";
import { toast } from "react-toastify";

const libraries = ['places', 'drawing', 'geometry'];

const center = {
  lat: 10.6620, // Default to Pollachi
  lng: 77.0065,
};

const options = {
  disableDefaultUI: false,
  zoomControl: true,
};

export default function GeofenceMap() {
  const { userProfile } = useProfile();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    version: "3.64",
    libraries: libraries as any
  });

  const [geofences, setGeofences] = useState<any[]>([]); 
  const [testLocation, setTestLocation] = useState<{lat: number, lng: number} | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<'available' | 'unavailable' | null>(null); 
  const [matchedZones, setMatchedZones] = useState<any[]>([]);
  const [routesList, setRoutesList] = useState<any[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [isExtendMode, setIsExtendMode] = useState(false);
  const [redrawZoneId, setRedrawZoneId] = useState<string | null>(null);

  useEffect(() => {
    listRoute().then((res: any) => {
      setRoutesList(res.data || []);
    });
  }, []);

  const drawingManagerRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const searchBoxRef = useRef<any>(null);

  const clearMap = () => {
    setGeofences([]);
    setTestLocation(null);
    setDeliveryStatus(null);
    setMatchedZones([]);
    setSelectedZoneId(null);
    setRedrawZoneId(null);
  };

  const deleteGeofence = (id: string) => {
    setGeofences(prev => prev.filter(g => g.id !== id));
  };

  const updateGeofencePrice = (id: string, price: string) => {
    setGeofences(prev => prev.map(g => g.id === id ? { ...g, price: parseFloat(price) || 0 } : g));
  };

  const updateGeofenceRoute = (id: string, routeId: any, routeName: string) => {
    setGeofences(prev => prev.map(g => g.id === id ? { ...g, name: routeName, route_id: routeId } : g));
  };

  const handleZoneUpdate = (id: string, type: string, data: any) => {
    setGeofences(prev => prev.map(g => g.id === id ? { ...g, ...data } : g));
  };

  const onPolygonLoad = (id: string, poly: google.maps.Polygon) => {
    const path = poly.getPath();
    const update = () => {
      const coords = [];
      for (let i = 0; i < path.getLength(); i++) {
        coords.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
      }
      handleZoneUpdate(id, 'polygon', { 
        coordinates: coords, 
        area: window.google.maps.geometry.spherical.computeArea(path) 
      });
    };
    path.addListener('set_at', update);
    path.addListener('insert_at', update);
    path.addListener('remove_at', update);
  };

  const onPolylineLoad = (id: string, line: google.maps.Polyline) => {
    const path = line.getPath();
    const update = () => {
      const coords = [];
      for (let i = 0; i < path.getLength(); i++) {
        coords.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
      }
      handleZoneUpdate(id, 'polyline', { coordinates: coords });
    };
    path.addListener('set_at', update);
    path.addListener('insert_at', update);
    path.addListener('remove_at', update);
  };

  const onCircleLoad = (id: string, circle: google.maps.Circle) => {
    const update = () => {
      const center = circle.getCenter();
      if (center) {
        handleZoneUpdate(id, 'circle', { 
          center: { lat: center.lat(), lng: center.lng() }, 
          radius: circle.getRadius() 
        });
      }
    };
    circle.addListener('center_changed', update);
    circle.addListener('radius_changed', update);
  };

  const onRectangleLoad = (id: string, rect: google.maps.Rectangle) => {
    const update = () => {
      const bounds = rect.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        handleZoneUpdate(id, 'rectangle', { 
          coordinates: { north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() } 
        });
      }
    };
    rect.addListener('bounds_changed', update);
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (isExtendMode && selectedZoneId && e.latLng) {
      const clickedLat = e.latLng.lat();
      const clickedLng = e.latLng.lng();
      setGeofences(prev => prev.map(fence => {
        if (fence.id === selectedZoneId) {
          const newCoords = [...(fence.coordinates || []), { lat: clickedLat, lng: clickedLng }];
          const newArea = fence.type === 'polygon' ? window.google.maps.geometry.spherical.computeArea(newCoords.map(c => new window.google.maps.LatLng(c.lat, c.lng))) : 0;
          return { ...fence, coordinates: newCoords, area: newArea };
        }
        return fence;
      }));
      return;
    }
    
    if (e.latLng) {
      setTestLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      setDeliveryStatus(null); 
      setMatchedZones([]);
    }
  };

  const checkDeliveryAvailability = () => {
    if (geofences.length === 0 || !testLocation) return;
    const point = new window.google.maps.LatLng(testLocation.lat, testLocation.lng);
    const matches: any[] = [];

    geofences.forEach(fence => {
      let isAvailable = false;
      let dist = 0;
      const fencePoint = fence.type === 'marker' ? fence.center : (fence.center || (fence.coordinates && fence.coordinates[0]));
      const fenceLatLng = new window.google.maps.LatLng(fencePoint.lat, fencePoint.lng);

      if (fence.type === 'polygon') {
        const poly = new window.google.maps.Polygon({ paths: fence.coordinates });
        isAvailable = window.google.maps.geometry.poly.containsLocation(point, poly);
        dist = window.google.maps.geometry.spherical.computeDistanceBetween(point, fenceLatLng);
      } else if (fence.type === 'circle') {
        dist = window.google.maps.geometry.spherical.computeDistanceBetween(point, fenceLatLng);
        isAvailable = dist <= fence.radius;
      } else if (fence.type === 'marker') {
        dist = window.google.maps.geometry.spherical.computeDistanceBetween(point, fenceLatLng);
        isAvailable = dist <= 50;
      } else if (fence.type === 'rectangle') {
        const bounds = new window.google.maps.LatLngBounds(
          { lat: fence.coordinates.south, lng: fence.coordinates.west },
          { lat: fence.coordinates.north, lng: fence.coordinates.east }
        );
        isAvailable = bounds.contains(point);
      } else if (fence.type === 'polyline') {
        const line = new window.google.maps.Polyline({ path: fence.coordinates });
        isAvailable = window.google.maps.geometry.poly.isLocationOnEdge(point, line, 10e-5);
      }

      if (isAvailable) {
        matches.push({ ...fence, distance: Math.round(dist) });
      }
    });

    setMatchedZones(matches);
    setDeliveryStatus(matches.length > 0 ? 'available' : 'unavailable');
  };

  useEffect(() => {
    if (testLocation) {
      checkDeliveryAvailability();
    }
  }, [geofences, testLocation]);

  const handleSaveAll = () => {
    if (geofences.length > 0) {
      const payload = geofences.map(g => ({
        name: g.name,
        route_id: g.route_id,
        type: g.type,
        price: g.price,
        created_by: userProfile?.userId,
        coordinates: g.type === 'polygon' || g.type === 'rectangle' || g.type === 'marker' || g.type === 'polyline' ? g.coordinates : g.center,
        radius: g.type === 'circle' ? g.radius : null,
        store_id: 1
      }));

      createDeliveryLocation(payload)
        .then(res => {
          if (res?.status) {
            toast.success(res?.message, { autoClose: 3000 });
            clearMap();
          } else {
            toast.error(res?.message || "Failed to save", { autoClose: 3000 });
          }
        })
        .catch(err => {
          toast.error("Failed to save. Check console.", { autoClose: 3000 });
        });
    }
  };

  if (loadError) return <div>Error loading maps: {loadError.message}</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={9}>
              <Card>
                <CardBody className="p-0">
                  <div className="geofence-map-wrapper">
                    <GoogleMap
                      mapContainerClassName="geofence-map-container"
                      center={center}
                      zoom={13}
                      onLoad={(map) => { mapRef.current = map; }}
                      options={options}
                      onClick={handleMapClick}
                    >
                      <StandaloneSearchBox
                        onLoad={ref => searchBoxRef.current = ref}
                        onPlacesChanged={() => {
                          const places = searchBoxRef.current.getPlaces();
                          if (places && places.length > 0) {
                            const place = places[0];
                            const location = place.geometry.location;
                            mapRef.current?.panTo({ lat: location.lat(), lng: location.lng() });
                            mapRef.current?.setZoom(15);
                          }
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search for places..."
                          style={{
                            boxSizing: `border-box`, border: `1px solid transparent`, width: `240px`, height: `40px`, padding: `0 12px`, borderRadius: `3px`, boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`, fontSize: `14px`, outline: `none`, textOverflow: `ellipses`, position: "absolute", left: "10px", top: "10px", zIndex: 5
                          }}
                        />
                      </StandaloneSearchBox>

                      <DrawingManagerF
                        onLoad={(manager) => { drawingManagerRef.current = manager; }}
                        onOverlayComplete={(e) => {
                          const id = redrawZoneId || Date.now().toString();
                          let newFence: any = { id, price: 0, name: `Zone ${geofences.length + 1}` };
                          
                          if (redrawZoneId) {
                            const existing = geofences.find(g => g.id === redrawZoneId);
                            if (existing) newFence = { ...existing };
                          }

                          if (e.type === 'polygon' && e.overlay) {
                            const poly = e.overlay as google.maps.Polygon;
                            const path = poly.getPath();
                            const coords = [];
                            for (let i = 0; i < path.getLength(); i++) {
                              coords.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
                            }
                            newFence = { ...newFence, type: 'polygon', coordinates: coords, area: window.google.maps.geometry.spherical.computeArea(path) };
                          } else if (e.type === 'circle' && e.overlay) {
                            const circle = e.overlay as google.maps.Circle;
                            const center = circle.getCenter();
                            if (center) newFence = { ...newFence, type: 'circle', center: { lat: center.lat(), lng: center.lng() }, radius: circle.getRadius() };
                          } else if (e.type === 'marker' && e.overlay) {
                            const marker = e.overlay as google.maps.Marker;
                            const pos = marker.getPosition();
                            if (pos) newFence = { ...newFence, type: 'marker', center: { lat: pos.lat(), lng: pos.lng() }, coordinates: { lat: pos.lat(), lng: pos.lng() } };
                          } else if (e.type === 'rectangle' && e.overlay) {
                            const rect = e.overlay as google.maps.Rectangle;
                            const bounds = rect.getBounds();
                            if (bounds) {
                              const ne = bounds.getNorthEast();
                              const sw = bounds.getSouthWest();
                              newFence = { ...newFence, type: 'rectangle', coordinates: { north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() } };
                            }
                          } else if (e.type === 'polyline' && e.overlay) {
                            const line = e.overlay as google.maps.Polyline;
                            const path = line.getPath();
                            const coords = [];
                            for (let i = 0; i < path.getLength(); i++) {
                              coords.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
                            }
                            newFence = { ...newFence, type: 'polyline', coordinates: coords };
                          }

                          if (redrawZoneId) {
                            setGeofences(prev => prev.map(g => g.id === redrawZoneId ? newFence : g));
                            setRedrawZoneId(null);
                          } else {
                            setGeofences(prev => [...prev, newFence]);
                          }

                          if (e.overlay) e.overlay.setMap(null);
                          if (drawingManagerRef.current) drawingManagerRef.current.setDrawingMode(null);
                        }}
                        options={{
                          drawingControl: !isExtendMode && !redrawZoneId,
                          drawingControlOptions: {
                            position: window.google.maps.ControlPosition.TOP_CENTER,
                            drawingModes: [
                              window.google.maps.drawing.OverlayType.POLYGON, 
                              window.google.maps.drawing.OverlayType.CIRCLE,
                              window.google.maps.drawing.OverlayType.MARKER,
                              window.google.maps.drawing.OverlayType.RECTANGLE,
                              window.google.maps.drawing.OverlayType.POLYLINE
                            ],
                          },
                          polygonOptions: { fillColor: '#2ae', fillOpacity: 0.4, strokeWeight: 2, strokeColor: '#2ae', editable: true, draggable: true },
                          circleOptions: { fillColor: '#F59E0B', fillOpacity: 0.4, strokeWeight: 2, strokeColor: '#F59E0B', editable: true, draggable: true },
                          rectangleOptions: { fillColor: '#6B7280', fillOpacity: 0.4, strokeWeight: 2, strokeColor: '#6B7280', editable: true, draggable: true },
                          polylineOptions: { strokeWeight: 3, strokeColor: '#10B981', editable: true, draggable: true }
                        }}
                      />

                      {geofences.map(fence => (
                        <React.Fragment key={fence.id}>
                          {fence.id !== redrawZoneId && (
                            <>
                              {fence.type === 'polygon' && (
                                <PolygonF 
                                  paths={fence.coordinates} 
                                  onLoad={(p) => onPolygonLoad(fence.id, p)}
                                  onClick={handleMapClick}
                                  options={{ fillColor: fence.id === selectedZoneId ? '#3B82F6' : '#2ae', fillOpacity: 0.4, strokeWeight: 2, strokeColor: fence.id === selectedZoneId ? '#3B82F6' : '#2ae', editable: true, draggable: true }}
                                />
                              )}
                              {fence.type === 'circle' && (
                                <CircleF 
                                  center={fence.center} 
                                  radius={fence.radius} 
                                  onLoad={(c) => onCircleLoad(fence.id, c)}
                                  onClick={handleMapClick}
                                  options={{ fillColor: '#F59E0B', fillOpacity: 0.4, strokeWeight: 2, strokeColor: '#F59E0B', editable: true, draggable: true }}
                                />
                              )}
                              {fence.type === 'polyline' && (
                                <PolylineF 
                                  path={fence.coordinates} 
                                  onLoad={(l) => onPolylineLoad(fence.id, l)}
                                  onClick={handleMapClick}
                                  options={{ strokeWeight: 3, strokeColor: '#10B981', editable: true, draggable: true }}
                                />
                              )}
                              {fence.type === 'rectangle' && (
                                <RectangleF 
                                  bounds={fence.coordinates} 
                                  onLoad={(r) => onRectangleLoad(fence.id, r)}
                                  onClick={handleMapClick}
                                  options={{ fillColor: '#6B7280', fillOpacity: 0.4, strokeWeight: 2, strokeColor: '#6B7280', editable: true, draggable: true }}
                                />
                              )}
                              {fence.type === 'marker' && (
                                <MarkerF 
                                  position={fence.center} 
                                  draggable={true}
                                  onDragEnd={(e) => handleZoneUpdate(fence.id, 'marker', { center: { lat: e.latLng?.lat(), lng: e.latLng?.lng() }, coordinates: { lat: e.latLng?.lat(), lng: e.latLng?.lng() } })}
                                  onClick={handleMapClick}
                                />
                              )}
                            </>
                          )}
                        </React.Fragment>
                      ))}

                      {testLocation && (
                        <MarkerF 
                          position={testLocation}
                          label={{ text: "?", color: "white", fontWeight: "bold" }}
                        />
                      )}
                    </GoogleMap>
                    <div style={{ position: "absolute", top: "10px", right: "10px", zIndex: 1, display: "flex", gap: "8px" }}>
                      <Button color="success" size="sm" className="fw-bold" onClick={handleSaveAll} disabled={geofences.length === 0 || geofences.some(g => !g.route_id)}>Save Location</Button>
                      <Button color="primary" size="sm" className="fw-bold" onClick={clearMap} disabled={geofences.length === 0}>Reset</Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Active Zones ({geofences.length})</h5>
                  <Button color="link" className="text-danger p-0 text-decoration-none" size="sm" onClick={clearMap} disabled={geofences.length === 0}>Clear All</Button>
                </CardHeader>
                <CardBody style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  {geofences.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {geofences.map((fence) => (
                        <div key={fence.id} className="border rounded p-2 bg-light shadow-sm">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="w-100 me-2">
                              <Input
                                type="select"
                                bsSize="sm"
                                className="fw-bold mb-1"
                                value={fence.route_id || ""}
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  const route = routesList.find(r => r.id == selectedId);
                                  if(route) updateGeofenceRoute(fence.id, route.id, route.name);
                                }}
                              >
                                <option value="">Select Route</option>
                                {routesList.map((route: any) => (
                                  <option key={route.id} value={route.id}>{route.name}</option>
                                ))}
                              </Input>
                              <small className="text-muted d-block">
                                {fence.type === 'circle' ? `Radius: ${Math.round(fence.radius)}m` : fence.type === 'polygon' ? `Area: ${(fence.area / 1000000).toFixed(2)} km²` : fence.type === 'marker' ? 'Marker Point' : fence.type === 'polyline' ? 'Path/Line' : 'Rectangle Area'}
                              </small>
                            </div>
                            <div className="d-flex gap-1 align-items-center">
                              <Button 
                                color={redrawZoneId === fence.id ? "warning" : "light"} 
                                size="sm" className="border-0 shadow-sm" title="Redraw/Extend"
                                onClick={() => {
                                  if (redrawZoneId === fence.id) {
                                    setRedrawZoneId(null);
                                    if (drawingManagerRef.current) drawingManagerRef.current.setDrawingMode(null);
                                  } else {
                                    setRedrawZoneId(fence.id);
                                    setIsExtendMode(false);
                                    setSelectedZoneId(null);
                                    if (drawingManagerRef.current) {
                                      const mode = fence.type === 'polygon' ? window.google.maps.drawing.OverlayType.POLYGON :
                                                   fence.type === 'circle' ? window.google.maps.drawing.OverlayType.CIRCLE :
                                                   fence.type === 'marker' ? window.google.maps.drawing.OverlayType.MARKER :
                                                   fence.type === 'rectangle' ? window.google.maps.drawing.OverlayType.RECTANGLE :
                                                   window.google.maps.drawing.OverlayType.POLYLINE;
                                      drawingManagerRef.current.setDrawingMode(mode);
                                    }
                                  }
                                }}
                              >
                                <RefreshCw size={14} className={redrawZoneId === fence.id ? "text-white" : "text-warning"} />
                              </Button>
                              <Button 
                                color={selectedZoneId === fence.id && isExtendMode ? "success" : "light"} 
                                size="sm" className="border-0 shadow-sm" title="Extend (Click Map)"
                                onClick={() => {
                                  if (selectedZoneId === fence.id) {
                                    setIsExtendMode(!isExtendMode);
                                  } else {
                                    setSelectedZoneId(fence.id);
                                    setIsExtendMode(true);
                                    setRedrawZoneId(null);
                                  }
                                }}
                                disabled={fence.type === 'circle' || fence.type === 'rectangle'}
                              >
                                <PlusCircle size={14} className={selectedZoneId === fence.id && isExtendMode ? "text-white" : "text-primary"} />
                              </Button>
                              <Button color="light" size="sm" className="text-danger border-0 shadow-sm" onClick={() => deleteGeofence(fence.id)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                          <InputGroup size="sm">
                            <span className="input-group-text">₹</span>
                            <Input type="number" placeholder="Price" value={fence.price} onChange={(e) => updateGeofencePrice(fence.id, e.target.value)} min="0" />
                          </InputGroup>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center my-4 fst-italic">Draw circles or polygons on the map to create delivery zones.</p>
                  )}
                </CardBody>
              </Card>

              <Card className="mt-3">
                <CardHeader><h5 className="card-title mb-0">Delivery Check</h5></CardHeader>
                <CardBody className="text-center p-3">
                  {testLocation ? (
                    <>
                      <Button color="primary" size="sm" className="mb-3 w-100" onClick={checkDeliveryAvailability}>Check Availability</Button>
                      {deliveryStatus === 'available' && (
                        <div className="alert alert-success p-2 mb-0 text-start small">
                          <div className="d-flex align-items-center gap-2 mb-2"><CheckCircle size={14} /><strong>Available!</strong></div>
                          <ul className="list-unstyled m-0">
                            {matchedZones.map((match, idx) => (
                              <li key={idx} className="d-flex justify-content-between border-top pt-1 mt-1"><span>{match.name}</span><strong>₹{match.price}</strong></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {deliveryStatus === 'unavailable' && (
                        <div className="alert alert-danger p-2 mb-0 d-flex align-items-center justify-content-center gap-2 small"><XCircle size={14} /><span>Not in zone</span></div>
                      )}
                    </>
                  ) : (
                    <small className="text-muted">Click on the map to set a test location.</small>
                  )}
                </CardBody>
              </Card>

              <div className="d-grid mt-3">
                <Button color="primary" className="btn-label" disabled={geofences.length === 0 || geofences.some(g => !g.route_id)} onClick={handleSaveAll}>
                  <i className="ri-save-line label-icon align-middle fs-16 me-2"></i> Save All Zones
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
